import { fileFormatFromName } from "../shared/fileFormat";

/**
 * Classifies a chat attachment so the UI can pick a thumbnail strategy:
 * images render the image, PDFs render the first page, everything else falls
 * back to a generic document icon.
 *
 * Decides by MIME type first (most reliable), then by file extension.
 */
export type AttachmentKind = "image" | "pdf" | "other";

const IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "gif", "webp", "bmp", "tiff"]);

export function attachmentKind(fileName: string, mimeType?: string): AttachmentKind {
    const mime = mimeType?.toLowerCase() ?? "";
    if (mime === "application/pdf") return "pdf";
    if (mime.startsWith("image/")) return "image";

    const dot = fileName.lastIndexOf(".");
    const ext = dot >= 0 ? fileName.slice(dot + 1).toLowerCase() : "";
    if (ext === "pdf") return "pdf";
    if (IMAGE_EXTENSIONS.has(ext)) return "image";

    return "other";
}

/**
 * Backend thumbnail endpoint for a stored file. Returns a small, long-cached JPEG
 * (~256px) for image + PDF uploads, or 404 for types that can't be previewed.
 * The `<AttachmentThumbnail>` component falls back to the document icon on error.
 */
export const attachmentThumbnailUrl = (fileId: string): string =>
    `/api/ai/file/${fileId}/thumbnail`;

/**
 * What clicking an attachment does, decided once for the message box and for sent
 * messages: PDFs and images open in the file viewer, a schedule file (.mpp, .xer, an MS
 * Project export) opens the Schedule viewer, and every other file downloads the original.
 */
export type AttachmentOpenAction = "view" | "schedule" | "download";

export function attachmentOpenAction(fileName: string, mimeType?: string): AttachmentOpenAction {
    if (attachmentKind(fileName, mimeType) !== "other") return "view";
    if (fileFormatFromName(fileName)?.format === "schedule" || /ms-?project/i.test(mimeType ?? ""))
        return "schedule";
    return "download";
}
